import { Box, DataList, Flex, Separator } from '@volue/wave-react';
import React from 'react';

import type { BusbarShortCircuitDetails } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import type { ElectricCurrentType } from '@/features/execute-calculation-set/overmind/calculation/domain/ElectricCurrent';
import type { EvaluatedResult } from '@/features/execute-calculation-set/overmind/calculation/evaluation/EvaluatedResult';
import { useAppState } from '@/overmind';

import type { SelectedGridElement } from '../../../overmind/calculation';
import { EvaluatedResultComponent } from '../../EvaluatedResultComponent';
import { EvaluatedValue } from '../../MediumVoltageCalculation/LoadFlowSummaryCalculationResults/EvaluatedCalculationResult';
import { LoadFlowDetailsHeader } from './LoadFlowDetailsHeader';

export type SelectedBusbar = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'Busbar'>;
};

export const BusbarDetails: React.FunctionComponent<{
  busbar: SelectedBusbar;
}> = ({ busbar }) => {
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;
  const getLoadSourceValue = (loadSource?: string) =>
    loadSource == null ? '' : `(${loadSource})`;

  return (
    <div data-testid={`busbar-${busbar.gridElement.objectId}`}>
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
        tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW') &&
        busbar.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' && (
          <>
            {busbar.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <LoadFlowDetailsHeader
                  scenarioName={
                    busbar.gridElement.loadflow.details.scenarioName
                  }
                />
                {
                  <DataList className="mHxl" ratio={[3, 1]}>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Voltage'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            busbar.gridElement.loadflow.details.results.voltage
                          }
                        />
                      </Flex>
                    </Flex>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Voltage Drop'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            busbar.gridElement.loadflow.details.results
                              .voltageDiffPercent
                          }
                          unitLabel
                        />
                      </Flex>
                    </Flex>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Power Factor'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            busbar.gridElement.loadflow.details.results
                              .voltageCosinePhi
                          }
                        />
                      </Flex>
                    </Flex>
                    {busbar.gridElement.loadflow.details.inputs !==
                      undefined && ( // TODO: Introduce separate type check instead of undefined?
                      <>
                        <Separator />
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>
                            {'Active Power '}
                            {getLoadSourceValue(
                              busbar.gridElement.loadflow.details.inputs
                                .loadSource
                            )}
                          </DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            {busbar.gridElement.loadflow.details.inputs.p.toString()}
                          </Flex>
                        </Flex>
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>
                            {'Reactive Power '}
                            {getLoadSourceValue(
                              busbar.gridElement.loadflow.details.inputs
                                .loadSource
                            )}
                          </DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            {busbar.gridElement.loadflow.details.inputs.q.toString()}
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </DataList>
                }
              </Flex>
            )}
          </>
        )}
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT') &&
        busbar.gridElement.shortCircuit.type === 'SHORT_CIRCUIT_DETAILS' && (
          <>
            {busbar.gridElement.shortCircuit.type ===
              'SHORT_CIRCUIT_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
                  {'Short Circuit'}
                </Box>
                {busbar.gridElement.objectType === 'Busbar' &&
                  busbar.gridElement.shortCircuit.evaluatedResults
                    .objectType === 'Busbar' && (
                    <DataList className="mHxl" ratio={[3, 1]}>
                      <Phase1Min
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase1Max
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase2Min
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase3Max
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                    </DataList>
                  )}
              </Flex>
            )}
          </>
        )}
    </div>
  );
};

function EvaluatedDataListItem({
  label,
  value
}: {
  label: string;
  value: EvaluatedResult<ElectricCurrentType>;
}): JSX.Element | null {
  return (
    <Flex as={DataList.Row} main="space-between">
      <DataList.Label>{label}</DataList.Label>
      <Flex main={'end'} as={DataList.Value}>
        <EvaluatedValue value={value.value.toString()} status={value.status} />
      </Flex>
    </Flex>
  );
}

function Phase1Min({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Min" value={results.phase1Min.result} />
  ) : null;
}

function Phase1Max({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Max" value={results.phase1Max.result} />
  ) : null;
}

function Phase2Min({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase2Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase2Min" value={results.phase2Min.result} />
  ) : null;
}

function Phase3Max({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase3Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase3Max" value={results.phase3Max.result} />
  ) : null;
}
